import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';
import SMSTexting from './SMSTexting';

function MainPage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState(0);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Scroll reveal effect
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          console.log('Revealing element:', entry.target.className);
          entry.target.classList.add('reveal');
        }
      });
    }, observerOptions);

    // Small delay to ensure DOM is ready
    const initializeObserver = () => {
      // Observe all sections that should be revealed
      const sectionsToReveal = document.querySelectorAll('.scroll-reveal, .scroll-reveal-heading, .scroll-reveal-widget, .scroll-reveal-text');
      
      console.log('Found elements to observe:', sectionsToReveal.length);
      
      sectionsToReveal.forEach(section => {
        observer.observe(section);
      });

      // Fallback: If elements are in viewport but not revealed after 2 seconds, show them
      setTimeout(() => {
        sectionsToReveal.forEach(section => {
          const rect = section.getBoundingClientRect();
          const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
          if (isInViewport && !section.classList.contains('reveal')) {
            section.classList.add('reveal');
          }
        });
      }, 2000);
    };

    // Use setTimeout to ensure DOM is fully rendered
    const timeoutId = setTimeout(initializeObserver, 100);

    return () => {
      clearTimeout(timeoutId);
      const sectionsToReveal = document.querySelectorAll('.scroll-reveal, .scroll-reveal-heading, .scroll-reveal-widget, .scroll-reveal-text');
      sectionsToReveal.forEach(section => {
        observer.unobserve(section);
      });
    };
  }, []);

  // Define multiple email templates
  const emailTemplates = [
    {
      id: 0,
      title: "Purchase Agreement",
      subject: "Create Purchase Agreement",
      body: "Hi! Please create a purchase agreement for 123 Main Street. The buyer is John Smith, purchase price $450,000, closing date March 15th. Send to buyer for signature when ready.\n\nNote: Please attach any relevant documents (example.pdf) to this email and replace this with your request!",
      attachment: "📎 example.pdf",
      preview: "Hi! Please create a purchase agreement for 123 Main Street. The buyer is John Smith, purchase price $450,000, closing date March 15th. Send to buyer for signature when ready."
    },
    {
      id: 1,
      title: "Listing Agreement",
      subject: "Create Listing Agreement",
      body: "Hi! Please create a listing agreement for 456 Oak Avenue. The seller is Jane Doe, listing price $525,000, 6-month listing period. Please include standard commission terms.\n\nNote: Please attach any relevant documents (listing_docs.pdf) to this email and replace this with your request!",
      attachment: "📎 listing_docs.pdf",
      preview: "Hi! Please create a listing agreement for 456 Oak Avenue. The seller is Jane Doe, listing price $525,000, 6-month listing period. Please include standard commission terms."
    },
    {
      id: 2,
      title: "Lease Agreement",
      subject: "Create Lease Agreement",
      body: "Hi! Please create a lease agreement for 789 Pine Street, Unit 2B. Tenant: Mike Johnson, monthly rent $2,200, 12-month lease starting April 1st. Security deposit $2,200.\n\nNote: Please attach any relevant documents (lease_forms.pdf) to this email and replace this with your request!",
      attachment: "📎 lease_forms.pdf",
      preview: "Hi! Please create a lease agreement for 789 Pine Street, Unit 2B. Tenant: Mike Johnson, monthly rent $2,200, 12-month lease starting April 1st. Security deposit $2,200."
    },
    {
      id: 3,
      title: "Disclosure Forms",
      subject: "Create Property Disclosure Forms",
      body: "Hi! Please create disclosure forms for 321 Maple Drive. Property built in 1995, recent roof replacement, updated electrical. Need seller disclosure and lead paint disclosure.\n\nNote: Please attach any relevant documents (property_info.pdf) to this email and replace this with your request!",
      attachment: "📎 property_info.pdf",
      preview: "Hi! Please create disclosure forms for 321 Maple Drive. Property built in 1995, recent roof replacement, updated electrical. Need seller disclosure and lead paint disclosure."
    }
  ];

  const handleEmailClick = (template) => {
    const mailtoLink = `mailto:hi@estateparrot.com?subject=${encodeURIComponent(template.subject)}&body=${encodeURIComponent(template.body)}`;
    
    // Try to open email client
    window.open(mailtoLink);
    
    // Also copy to clipboard as backup
    navigator.clipboard.writeText(`To: hi@estateparrot.com\nSubject: ${template.subject}\n\n${template.body}`);
  };

  return (
    <div className="App">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-logo">
            <img src={require('./logo_no_text.png')} alt="Estate Parrot Logo" className="logo-image" />
            <span className="logo-text">Estate Parrot</span>
          </div>

          <div className={`nav-menu ${isMenuOpen ? 'active' : ''}`}>
            <a href="#features" className="nav-link" onClick={(e) => {e.preventDefault(); document.getElementById('features').scrollIntoView({behavior: 'smooth'});}}>Features</a>
            <a href="#pricing" className="nav-link" onClick={(e) => {e.preventDefault(); document.getElementById('pricing').scrollIntoView({behavior: 'smooth'});}}>Pricing</a>
          </div>

          <div className="nav-toggle" onClick={toggleMenu}>
            <span className="bar"></span>
            <span className="bar"></span>
            <span className="bar"></span>
          </div>
        </div>
      </nav>

      {/* Feature Info Banner */}
      <div className="info-banner" onClick={() => {
        const subject = "Feature Details Request";
        const body = "Hi, can you send me a detailed explanation of all your features? Thanks!";
        const mailtoLink = `mailto:hi@estateparrot.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        window.open(mailtoLink);
      }}>
        <div className="container">
          <p>For a detailed explanation of all the features just email us at <strong>hi@estateparrot.com</strong></p>
        </div>
      </div>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-logo">
              <img src={require('./logo_no_text.png')} alt="Estate Parrot Logo" className="hero-logo-image" />
            </div>
            <h1 className="hero-title">
              Reclaim Your Time With a Single Email
            </h1>
            <p className="hero-subtitle">
              Estate Parrot fills PDFs, sends for signatures, and more. Get contracts done in 4-10 minutes from your phone, anywhere, anytime.
            </p>

          </div>
          <div className="hero-image">
            <div className="estate-ui">
              <div className="ui-window">
                <div className="ui-header">
                  <div className="ui-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
                <div className="ui-content">
                  <div className="ui-message">
                    <p className="ui-test-prompt">Want to test it out?</p>
                  </div>
                  
                  {/* Email Template Selector */}
                  <div className="email-template-selector">
                    {emailTemplates.map((template, index) => (
                      <button
                        key={template.id}
                        className={`template-tab ${selectedEmailTemplate === index ? 'active' : ''}`}
                        onClick={() => setSelectedEmailTemplate(index)}
                      >
                        {template.title}
                      </button>
                    ))}
                  </div>
                  
                  <div className="ui-example-email" onClick={() => handleEmailClick(emailTemplates[selectedEmailTemplate])}>
                    <div className="email-header">To: hi@estateparrot.com</div>
                    <div className="email-body">{emailTemplates[selectedEmailTemplate].preview}</div>
                    <div className="email-attachment">{emailTemplates[selectedEmailTemplate].attachment}</div>
                    <div className="copy-hint">Click to open email client</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="undetectable scroll-reveal" id="features">
        <div className="container">
          <h2 className="section-title scroll-reveal-heading">What Estate Parrot Does</h2>
          <p className="section-subtitle scroll-reveal-text">Never experience the pain of PDF editors, DocuSign, or&nbsp;SmartMLS&nbsp;again.</p>

          <div className="undetectable-features">
            <div className="undetectable-card scroll-reveal-widget">
              <div className="card-image">
                <div className="meeting-mockup">
                  <div className="meeting-header">
                    <span className="meeting-title">PDF Builder</span>
                    <span className="meeting-participants">Auto-Fill</span>
                  </div>
                  <div className="meeting-content">
                    <div className="participant-grid">
                      <div className="participant"></div>
                      <div className="participant"></div>
                      <div className="participant"></div>
                      <div className="participant"></div>
                    </div>
                  </div>
                </div>
              </div>
              <h3 className="scroll-reveal-heading">Fills PDFs & Remembers Templates</h3>
              <p className="scroll-reveal-text">Autofills info from SmartMLS and tax records. Remembers your PDF templates for instant reuse.</p>
            </div>

            <div className="undetectable-card scroll-reveal-widget">
              <div className="card-image">
                <div className="screen-share-mockup">
                  <div className="screen-content">
                    <div className="presentation-slide">
                      <h4>Ready to Sign</h4>
                      <div className="chart-placeholder"></div>
                    </div>
                  </div>
                </div>
              </div>
              <h3 className="scroll-reveal-heading">Sends for Signatures Easily</h3>
              <p className="scroll-reveal-text">Skip DocuSign. Estate Parrot handles signatures and adds important events to your calendar automatically.</p>
            </div>

            <div className="undetectable-card scroll-reveal-widget">
              <div className="card-image">
                <div className="eye-tracking-mockup">
                  <div className="screen-overlay">
                    <div className="estate-window">
                      <div className="window-header"></div>
                      <div className="window-content">
                        <p>Reminder: Follow up</p>
                      </div>
                    </div>
                    <div className="eye-indicator"></div>
                  </div>
                </div>
              </div>
              <h3 className="scroll-reveal-heading">Daily Reminders</h3>
              <p className="scroll-reveal-text">Never miss deadlines again. Get daily reminders of tasks and signatures you need to complete.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing scroll-reveal" id="pricing">
        <div className="container">
          <h2 className="section-title scroll-reveal-heading">Pricing Plans</h2>
          
          <div className="pricing-highlights">
            <div className="highlight-box free-trial scroll-reveal-widget">
              <h3 className="scroll-reveal-heading">🎉 Try Free for 30 Days</h3>
              <p className="scroll-reveal-text">No credit card required</p>
            </div>
            <div className="highlight-box discount-code scroll-reveal-widget">
              <h3 className="scroll-reveal-heading">💰 Save $75 with Code</h3>
              <p className="scroll-reveal-text"><strong>SUPEREARLY</strong></p>
            </div>
          </div>
          
          <div className="pricing-table">
            <div className="pricing-plan scroll-reveal-widget">
              <div className="plan-header">
                <h3 className="plan-name scroll-reveal-heading">Basic</h3>
                <div className="plan-price">
                  <span className="original-price">$200</span>
                  <span className="discounted-price">$125</span>
                  <span className="plan-period">/user/month</span>
                </div>
              </div>
              <div className="plan-features">
                <ul>
                  <li className="scroll-reveal-text">✅ Fill PDFs</li>
                  <li className="scroll-reveal-text">✅ Signatures via Dropbox</li>
                  <li className="scroll-reveal-text">✅ Calendar Events</li>
                  <li className="scroll-reveal-text">✅ Daily Reminders</li>
                </ul>
              </div>
            </div>

            <div className="pricing-plan featured scroll-reveal-widget">
              <div className="plan-header">
                <h3 className="plan-name scroll-reveal-heading">Pro</h3>
                <div className="plan-price">
                  <span className="original-price">$300</span>
                  <span className="discounted-price">$225</span>
                  <span className="plan-period">/user/month</span>
                </div>
              </div>
              <div className="plan-features">
                <ul>
                  <li className="scroll-reveal-text">✅ All Basic Features</li>
                  <li className="scroll-reveal-text">✅ SMS Texting</li>
                  <li className="scroll-reveal-text">✅ Autofill Data to SmartMLS</li>
                  <li className="scroll-reveal-text">✅ Create Social Media Posts</li>
                </ul>
              </div>
            </div>

            <div className="pricing-plan scroll-reveal-widget">
              <div className="plan-header">
                <h3 className="plan-name scroll-reveal-heading">Team</h3>
                <div className="plan-price">
                  <span className="original-price">$250</span>
                  <span className="discounted-price">$175</span>
                  <span className="plan-period">/user/month</span>
                </div>
              </div>
              <div className="plan-features">
                <ul>
                  <li className="scroll-reveal-text">✅ All Pro Features</li>
                  <li className="scroll-reveal-text">✅ For 3+ People</li>
                  <li className="scroll-reveal-text">✅ Team Customization</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Coming Soon */}
      <section className="use-cases scroll-reveal">
        <div className="container">
          <h2 className="section-title scroll-reveal-heading">Future Features</h2>
          
          <div className="use-cases-content">
            <div className="use-case active">
              <div className="use-case-text">
                <h3 className="scroll-reveal-heading">These Include</h3>
                <p className="scroll-reveal-text"><strong>SmartMLS Listings</strong> - Create listings by describing properties and uploading photos</p>
                <p className="scroll-reveal-text"><strong>Text Messages</strong> - Texting on top of emailing for even faster communication</p>
                <p className="scroll-reveal-text"><strong>Social Media Posts</strong> - Automatically create Instagram and LinkedIn posts about your listings</p>
              </div>
              <div className="use-case-visual scroll-reveal-widget">
                <div className="email-mockup">
                  <div className="email-window">
                    <div className="email-header-bar">
                      <div className="email-dots">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                    <div className="email-content">
                      <div className="email-to">To: hi@estateparrot.com</div>
                      <div className="email-subject">Subject: Create MLS Listing</div>
                      <div className="email-message">
                        I need to create a listing for 456 Oak Avenue. 3 bed, 2 bath, 1,850 sq ft. Asking $380,000. Photos attached. Please create the SmartMLS listing.
                      </div>
                      <div className="email-response">
                        Listing created and published to SmartMLS automatically!
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Simple Footer */}
      <footer className="simple-footer scroll-reveal">
        <div className="container">
          <p>Estate Parrot 2025. All Rights Reserved</p>
      </div>
      </footer>

    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<MainPage />} />
        <Route path="/smstexting" element={<SMSTexting />} />
      </Routes>
    </Router>
  );
}

export default App; 